import { useMemo } from 'react';
import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, MapPin } from 'lucide-react';
import { fetchLocations } from '@/lib/api';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import {
  ScopedReportsPage,
  STANDARD_PERIOD_OPTIONS,
  type TabSpec,
} from '@/components/reports/ScopedReportsPage';
import { BookingHistoryTab } from './BookingHistoryTab';
import { LocationUnitsConsumedTab } from './LocationUnitsConsumedTab';
import { useAuthStore } from '@/store/auth';

const SITE_PERIOD_OPTIONS = [
  ...STANDARD_PERIOD_OPTIONS.filter((option) => option.id !== 'custom'),
  { id: '365d', label: '1 year', supported: true, kind: 'preset' as const, days: 365 },
  ...STANDARD_PERIOD_OPTIONS.filter((option) => option.id === 'custom'),
];

/**
 * Building Admin Reports — top-level page.
 *
 * Composes the shared ScopedReportsPage shell (commit 3) with two
 * tab bodies that consume the new admin/reports endpoints
 * (commits 4 + 5). Server-side scope plumbing handles the
 * building_admin persona transparently via the third_party_admin
 * branch in dashboardScope.ts → AccessAssignment.location_ids.
 *
 * Shell composition:
 *   - locationFilter='multi-only' — single-location building admins
 *     don't see a pointless one-option dropdown; admins with 2+
 *     assigned locations get the dropdown the operator + SP pages
 *     also show.
 *   - chromeOrder='filter-first' — matches SP's chrome order; reads
 *     well when the user lands on /reports as their primary surface.
 *   - bannerStack=null — no banner equivalent of SP's Connect
 *     banner; building admin onboarding lives elsewhere.
 *   - defaultTabId='booking-history' — the headline "what happened
 *     at my property" surface; Location Units is the rollup tier.
 *
 * Locations fetch: server-scoped to the caller's
 * AccessAssignment.location_ids via fetchLocations + the
 * third_party_admin branch. Empty array = no assigned locations
 * (defensive; ProductOps probably prevents this state, but if it
 * happens the dropdown won't render and tab bodies will return
 * empty results — no crash, no misleading "All locations" hint).
 */
export function BuildingAdminReportsPage(): JSX.Element {
  useRecentRoute('Reports');
  const userId = useAuthStore((state) => state.user?._id);

  const locationsQ = useQuery({
    queryKey: ['admin', 'building-admin-reports', 'locations', userId],
    queryFn: async () => {
      const first = await fetchLocations({ limit: 100 });
      const data = [...first.data];
      for (let page = 2; page <= first.meta.pages; page++) data.push(...(await fetchLocations({ limit: 100, page })).data);
      return { ...first, data };
    },
    staleTime: 5 * 60_000,
  });
  const locations = locationsQ.data?.data ?? [];

  // Tab inventory — 2 tabs, no coming-soon stubs. Each render
  // closure captures `locations` so the tab bodies can map
  // location_id → name without a per-tab fetch.
  const tabs: ReadonlyArray<TabSpec> = useMemo(() => [
    {
      id: 'booking-history',
      label: 'Booking History',
      icon: CalendarClock,
      render: ({ period, from, to, locationId, filters }) => (
        <BookingHistoryTab
          period={period}
          from={from}
          to={to}
          locationId={locationId}
          locationIds={filters.location_ids}
        />
      ),
    },
    {
      id: 'location-units',
      label: 'Compare Locations',
      icon: MapPin,
      render: ({ period, from, to, filters }) => (
        <LocationUnitsConsumedTab
          locationIds={filters.location_ids}
          period={period}
          from={from}
          to={to}
        />
      ),
    },
  ], []);

  return (
    <ScopedReportsPage
      title="Reports"
      subtitle="Sessions and energy consumption at your assigned properties."
      tabs={tabs}
      defaultTabId="booking-history"
      periodOptions={SITE_PERIOD_OPTIONS}
      defaultPeriodId="7d"
      locations={locations}
      locationFilter="multi-only"
      multiLocationFilter
      chromeOrder="filter-first"
    />
  );
}
